import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  DollarSign, 
  Calendar,
  Target,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Edit,
  Plus,
  Filter,
  Download,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import './ProjectDashboardReports.css';

const ProjectDashboardReports = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    // KPI Cards
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalBudget: 0,
    totalSpent: 0,
    averageProgress: 0,
    
    // Project Status Distribution
    statusDistribution: {
      planning: 0,
      active: 0,
      onHold: 0,
      completed: 0,
      cancelled: 0
    },
    
    // Priority Distribution
    priorityDistribution: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    },
    
    // Budget vs Actual
    budgetData: [],
    
    // Timeline Data
    timelineData: [],
    
    // Team Performance
    teamPerformance: [],
    
    // Recent Activities
    recentActivities: [],
    
    // Upcoming Milestones
    upcomingMilestones: [],
    
    // Risk Analysis
    riskAnalysis: {
      totalRisks: 0,
      highRisks: 0,
      mediumRisks: 0,
      lowRisks: 0
    },
    
    // Project Health
    projectHealth: {
      healthy: 0,
      atRisk: 0,
      critical: 0
    }
  });

  // Load report data
  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/dashboard`, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        // Fallback to mock data
        setReportData(getMockReportData());
        showError('Failed to load report data from server. Using sample data.');
      }
    } catch (err) {
      console.error('Error loading report data:', err);
      setReportData(getMockReportData());
      showError('Error loading report data. Using sample data.');
    } finally {
      setLoading(false);
    }
  };

  const getMockReportData = () => ({
    // KPI Cards
    totalProjects: 24,
    activeProjects: 12,
    completedProjects: 8,
    totalBudget: 2500000,
    totalSpent: 1800000,
    averageProgress: 68,
    
    // Project Status Distribution
    statusDistribution: {
      planning: 4,
      active: 12,
      onHold: 2,
      completed: 8,
      cancelled: 1
    },
    
    // Priority Distribution
    priorityDistribution: {
      low: 6,
      medium: 10,
      high: 6,
      critical: 2
    },
    
    // Budget vs Actual
    budgetData: [
      { name: 'Digital Transformation', budget: 500000, actual: 450000, variance: -50000 },
      { name: 'Infrastructure Upgrade', budget: 300000, actual: 320000, variance: 20000 },
      { name: 'Mobile App Development', budget: 200000, actual: 180000, variance: -20000 },
      { name: 'Security Enhancement', budget: 150000, actual: 160000, variance: 10000 },
      { name: 'Process Automation', budget: 100000, actual: 95000, variance: -5000 }
    ],
    
    // Timeline Data
    timelineData: [
      { month: 'Jan', planned: 5, completed: 3 },
      { month: 'Feb', planned: 8, completed: 6 },
      { month: 'Mar', planned: 12, completed: 10 },
      { month: 'Apr', planned: 15, completed: 12 },
      { month: 'May', planned: 18, completed: 15 },
      { month: 'Jun', planned: 20, completed: 18 }
    ],
    
    // Team Performance
    teamPerformance: [
      { name: 'Sarah Johnson', projects: 3, completion: 95, efficiency: 88 },
      { name: 'Mike Chen', projects: 2, completion: 92, efficiency: 85 },
      { name: 'Lisa Wang', projects: 4, completion: 89, efficiency: 82 },
      { name: 'David Rodriguez', projects: 2, completion: 94, efficiency: 90 },
      { name: 'Emma Davis', projects: 3, completion: 87, efficiency: 79 }
    ],
    
    // Recent Activities
    recentActivities: [
      {
        id: 1,
        type: 'project',
        title: 'Digital Transformation Phase 2 Completed',
        description: 'Successfully completed the second phase of digital transformation',
        time: '2 hours ago',
        status: 'success'
      },
      {
        id: 2,
        type: 'milestone',
        title: 'Infrastructure Upgrade Milestone',
        description: 'Reached 80% completion milestone',
        time: '4 hours ago',
        status: 'success'
      },
      {
        id: 3,
        type: 'risk',
        title: 'High Risk Identified',
        description: 'Budget overrun risk in Mobile App project',
        time: '6 hours ago',
        status: 'warning'
      },
      {
        id: 4,
        type: 'issue',
        title: 'Resource Allocation Issue',
        description: 'Team member unavailability affecting timeline',
        time: '8 hours ago',
        status: 'error'
      }
    ],
    
    // Upcoming Milestones
    upcomingMilestones: [
      {
        id: 1,
        projectName: 'Digital Transformation',
        milestone: 'Phase 3 Kickoff',
        dueDate: '2024-02-15',
        status: 'upcoming'
      },
      {
        id: 2,
        projectName: 'Mobile App Development',
        milestone: 'Beta Release',
        dueDate: '2024-02-20',
        status: 'upcoming'
      },
      {
        id: 3,
        projectName: 'Security Enhancement',
        milestone: 'Penetration Testing',
        dueDate: '2024-02-25',
        status: 'upcoming'
      }
    ],
    
    // Risk Analysis
    riskAnalysis: {
      totalRisks: 15,
      highRisks: 3,
      mediumRisks: 5,
      lowRisks: 7
    },
    
    // Project Health
    projectHealth: {
      healthy: 16,
      atRisk: 6,
      critical: 2
    }
  });

  const handleExportReport = () => {
    // Implement export functionality
    success('Report exported successfully!');
  };

  const handleRefreshReport = () => {
    loadReportData();
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div className="page-title-section">
            <h1 className="page-title">Project Dashboard Reports</h1>
            <p className="page-subtitle">Comprehensive project overview and analytics</p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/projects/reports')}
          >
            <ArrowLeft size={18} />
            Back to Reports
          </button>
          <div className="page-title-section">
            <h1 className="page-title">Project Dashboard Reports</h1>
            <p className="page-subtitle">
              Comprehensive project overview and performance analytics
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={handleRefreshReport}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-outline" onClick={handleExportReport}>
            <Download size={18} />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="page-stats-grid">
        <div className="page-stat-card">
          <div className="page-kpi-card">
            <div className="page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-icon-container color-1">
                  <BarChart3 size={24} className="page-kpi-icon color-1" />
                </div>
                <div className="page-kpi-text">
                  <div className="page-kpi-value color-1">
                    {reportData.totalProjects}
                  </div>
                  <div className="page-kpi-label">
                    Total Projects
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-kpi-card">
            <div className="page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-icon-container color-2">
                  <Activity size={24} className="page-kpi-icon color-2" />
                </div>
                <div className="page-kpi-text">
                  <div className="page-kpi-value color-2">
                    {reportData.activeProjects}
                  </div>
                  <div className="page-kpi-label">
                    Active Projects
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-kpi-card">
            <div className="page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-icon-container color-3">
                  <CheckCircle size={24} className="page-kpi-icon color-3" />
                </div>
                <div className="page-kpi-text">
                  <div className="page-kpi-value color-3">
                    {reportData.completedProjects}
                  </div>
                  <div className="page-kpi-label">
                    Completed
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-kpi-card">
            <div className="page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-icon-container color-4">
                  <DollarSign size={24} className="page-kpi-icon color-4" />
                </div>
                <div className="page-kpi-text">
                  <div className="page-kpi-value color-4">
                    ${(reportData.totalBudget / 1000000).toFixed(1)}M
                  </div>
                  <div className="page-kpi-label">
                    Total Budget
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-kpi-card">
            <div className="page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-icon-container color-5">
                  <TrendingUp size={24} className="page-kpi-icon color-5" />
                </div>
                <div className="page-kpi-text">
                  <div className="page-kpi-value color-5">
                    {reportData.averageProgress}%
                  </div>
                  <div className="page-kpi-label">
                    Avg Progress
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="reports-charts-section">
        <div className="row">
          {/* Project Status Distribution */}
          <div className="col-md-6">
            <div className="report-card">
              <div className="report-card-header">
                <h3 className="report-card-title">Project Status Distribution</h3>
                <p className="report-card-subtitle">Current status of all projects</p>
              </div>
              <div className="report-card-body">
                <div className="status-distribution">
                  {Object.entries(reportData.statusDistribution).map(([status, count]) => (
                    <div key={status} className="status-item">
                      <div className="status-info">
                        <span className="status-label">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                        <span className="status-count">{count}</span>
                      </div>
                      <div className="status-bar">
                        <div 
                          className="status-fill" 
                          style={{ 
                            width: `${(count / reportData.totalProjects) * 100}%`,
                            backgroundColor: getStatusColor(status)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Priority Distribution */}
          <div className="col-md-6">
            <div className="report-card">
              <div className="report-card-header">
                <h3 className="report-card-title">Priority Distribution</h3>
                <p className="report-card-subtitle">Project priority levels</p>
              </div>
              <div className="report-card-body">
                <div className="priority-distribution">
                  {Object.entries(reportData.priorityDistribution).map(([priority, count]) => (
                    <div key={priority} className="priority-item">
                      <div className="priority-info">
                        <span className="priority-label">{priority.charAt(0).toUpperCase() + priority.slice(1)}</span>
                        <span className="priority-count">{count}</span>
                      </div>
                      <div className="priority-bar">
                        <div 
                          className="priority-fill" 
                          style={{ 
                            width: `${(count / reportData.totalProjects) * 100}%`,
                            backgroundColor: getPriorityColor(priority)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Analysis */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="report-card">
              <div className="report-card-header">
                <h3 className="report-card-title">Budget vs Actual Analysis</h3>
                <p className="report-card-subtitle">Budget performance across projects</p>
              </div>
              <div className="report-card-body">
                <div className="budget-analysis-table">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Budget</th>
                        <th>Actual</th>
                        <th>Variance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.budgetData.map((project, index) => (
                        <tr key={index}>
                          <td className="project-name">{project.name}</td>
                          <td>${project.budget.toLocaleString()}</td>
                          <td>${project.actual.toLocaleString()}</td>
                          <td className={project.variance >= 0 ? 'text-danger' : 'text-success'}>
                            ${Math.abs(project.variance).toLocaleString()}
                            {project.variance >= 0 ? ' Over' : ' Under'}
                          </td>
                          <td>
                            <span className={`badge ${project.variance >= 0 ? 'badge-danger' : 'badge-success'}`}>
                              {project.variance >= 0 ? 'Over Budget' : 'On Track'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities & Milestones */}
      <div className="reports-activities-section">
        <div className="row">
          <div className="col-md-6">
            <div className="report-card">
              <div className="report-card-header">
                <h3 className="report-card-title">Recent Activities</h3>
                <p className="report-card-subtitle">Latest project activities and updates</p>
              </div>
              <div className="report-card-body">
                <div className="activities-list">
                  {reportData.recentActivities.map((activity) => (
                    <div key={activity.id} className="activity-item">
                      <div className="activity-icon">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="activity-content">
                        <div className="activity-title">{activity.title}</div>
                        <div className="activity-description">{activity.description}</div>
                        <div className="activity-time">{activity.time}</div>
                      </div>
                      <div className={`activity-status ${activity.status}`}>
                        {getActivityStatusIcon(activity.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="report-card">
              <div className="report-card-header">
                <h3 className="report-card-title">Upcoming Milestones</h3>
                <p className="report-card-subtitle">Important milestones in the next 30 days</p>
              </div>
              <div className="report-card-body">
                <div className="milestones-list">
                  {reportData.upcomingMilestones.map((milestone) => (
                    <div key={milestone.id} className="milestone-item">
                      <div className="milestone-info">
                        <div className="milestone-project">{milestone.projectName}</div>
                        <div className="milestone-name">{milestone.milestone}</div>
                        <div className="milestone-date">
                          <Calendar size={14} />
                          {new Date(milestone.dueDate).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="milestone-status">
                        <span className="badge badge-warning">Upcoming</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getStatusColor = (status) => {
  const colors = {
    planning: '#6B7280',
    active: '#3B82F6',
    onHold: '#F59E0B',
    completed: '#10B981',
    cancelled: '#EF4444'
  };
  return colors[status] || '#6B7280';
};

const getPriorityColor = (priority) => {
  const colors = {
    low: '#10B981',
    medium: '#F59E0B',
    high: '#EF4444',
    critical: '#DC2626'
  };
  return colors[priority] || '#6B7280';
};

const getActivityIcon = (type) => {
  const icons = {
    project: <BarChart3 size={16} />,
    milestone: <Target size={16} />,
    risk: <AlertTriangle size={16} />,
    issue: <AlertTriangle size={16} />
  };
  return icons[type] || <Activity size={16} />;
};

const getActivityStatusIcon = (status) => {
  const icons = {
    success: <CheckCircle size={16} />,
    warning: <AlertTriangle size={16} />,
    error: <AlertTriangle size={16} />
  };
  return icons[status] || <Activity size={16} />;
};

export default ProjectDashboardReports;
